import 'swiper/css';
import 'swiper/css/pagination';

import Grid2 from '@mui/material/Unstable_Grid2';
import { useId } from 'react';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ArticleCard from '@/components/BlogComponents/shared/ArticleCard/ArticleCard';
import ArticleHeroImage from '@/components/BlogComponents/shared/ArticleHeroImage/ArticleHeroImage';
import Icon, { IconName } from '@/components/Icon/Icon';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import type { BlogPageType } from '@/contentful/api/types';
import useViewPortSize from '@/hooks/useViewportSize';
import { trimStringEndingPunctuation } from '@/utils/trimStringEndingPunctuation';

import useStyles from './BlogHighlight.style';

export interface BlogHighlightProps {
  primaryArticle?: BlogPageType;
  articlesCollection: BlogPageType[];
}

export default function BlogHighlight({
  primaryArticle,
  articlesCollection,
}: BlogHighlightProps): React.JSX.Element {
  const { classes, cx } = useStyles();

  const prevElId = useId().replaceAll(':', '');
  const nextElId = useId().replaceAll(':', '');
  const paginationId = useId().replaceAll(':', '');
  const { isExtraSmall } = useViewPortSize();

  return (
    <SpacingContainer
      isFeatured
      className={classes.spacingContainer}
      data-testid='blogHighlight'
    >
      <GridContainer2 fixed>
        <div className={classes.container}>
          <Grid2>
            {primaryArticle && <ArticleHeroImage article={primaryArticle} />}
          </Grid2>
          {isExtraSmall ? (
            <>
              <Swiper
                navigation={{
                  prevEl: `#${prevElId}`,
                  nextEl: `#${nextElId}`,
                  disabledClass: classes.navigationButtonDisabled,
                }}
                className={classes.swiper}
                modules={[Navigation, Pagination]}
                slidesPerView={1}
                pagination={{
                  el: `#${paginationId}`,
                  bulletActiveClass: 'bullet-active',
                  bulletClass: 'bullet',
                  horizontalClass: classes.horizontal,
                  // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
                  renderBullet: (index: number, className: string) => {
                    return `<svg class="${className}" width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="4" r="4" fill="currentColor"/>
              </svg>`;
                  },
                }}
              >
                <div className={classes.cardContainer}>
                  {articlesCollection.map(
                    ({ blogTitle, abstract, path }, index) => (
                      <SwiperSlide
                        key={`${blogTitle}-${
                          // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                          index
                        }`}
                      >
                        <Grid2>
                          <ArticleCard
                            title={blogTitle}
                            body={`${trimStringEndingPunctuation(
                              abstract
                            )} ...`}
                            readMoreCta='Read More'
                            readMoreUrl={path || ''}
                          />
                        </Grid2>
                      </SwiperSlide>
                    )
                  )}
                </div>
              </Swiper>
              <div
                className={cx(
                  classes.navigation,
                  articlesCollection.length < 2 && classes.hidden
                )}
              >
                <div id={prevElId} className={classes.navigationButton}>
                  <Icon name={IconName.ARROW_LEFT} width={24} />
                </div>
                <div id={paginationId} className={classes.pagination}></div>
                <div id={nextElId} className={classes.navigationButton}>
                  <Icon name={IconName.ARROW_RIGHT} width={24} />
                </div>
              </div>
            </>
          ) : (
            <Grid2>
              <div className={classes.cardContainer}>
                {articlesCollection.map(
                  ({ blogTitle, abstract, path }, index) => (
                    <ArticleCard
                      key={`${blogTitle}-${
                        // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                        index
                      }`}
                      title={blogTitle}
                      body={`${trimStringEndingPunctuation(abstract)} ...`}
                      readMoreCta='Read More'
                      readMoreUrl={path || ''}
                    />
                  )
                )}
              </div>
            </Grid2>
          )}
        </div>
      </GridContainer2>
    </SpacingContainer>
  );
}
